import { useRef } from "react";
import Icon from "../Icon";
import { BtnPrimary } from "../Atoms";
import { getTipoColor, getTipoProd, prodBand, prodStats, fmtRatio, fmtHours } from "../../utils";


export default function ExportCuadernoModal({agenda, view="tabla", currentUser, agendaTipos=[], agendaTiposData=[], teams=[], notionItems=[], onClose}) {
  const iframeRef = useRef(null);
  const isDash = view === "dash";
  const totalH = agenda.reduce((s,a)=>s+(parseFloat(a.hours)||0),0);
  const byType = {};
  const knownTipo = tp => !!tp && agendaTipos.includes(tp);
  agenda.forEach(a=>{ const tp=knownTipo(a.type)?a.type:"Otros"; if(!byType[tp]) byType[tp]=[]; byType[tp].push(a); });
  const fmtD = d => d ? new Date(d).toLocaleDateString("es-ES",{day:"2-digit",month:"short",year:"numeric"}) : "—";
  const esc = s => String(s??"").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");

  const TEAM_CLR=["#6366f1","#0ea5e9","#10b981","#f59e0b","#ef4444","#8b5cf6","#ec4899","#14b8a6"];

  // ── Agregaciones para el informe de dashboard ──
  const otrosHours = agenda.filter(a=>!knownTipo(a.type)).reduce((s,a)=>s+(parseFloat(a.hours)||0),0);
  const prod = prodStats(agenda, agendaTiposData);
  const totalByTipo = [
    ...agendaTipos.map(tp=>({
      tipo:tp,
      color:getTipoColor(tp,agendaTipos,agendaTiposData),
      prod:getTipoProd(tp,agendaTiposData),
      hours:agenda.filter(a=>a.type===tp).reduce((s,a)=>s+(parseFloat(a.hours)||0),0),
    })),
    // Sin esto las horas sin tipo desaparecían del gráfico pero seguían en el total
    {tipo:"Otros", color:"var(--text3)", hours:otrosHours},
  ].filter(x=>x.hours>0).sort((a,b)=>b.hours-a.hours);

  const teamsData=(teams||[]).map((t,ti)=>{
    const tEntries=agenda.filter(a=>a.department===t.name);
    const th=tEntries.reduce((s,a)=>s+(parseFloat(a.hours)||0),0);
    const byTipo=[
      ...agendaTipos.map(tp=>({
        tipo:tp,
        hours:tEntries.filter(a=>a.type===tp).reduce((s,a)=>s+(parseFloat(a.hours)||0),0),
        color:getTipoColor(tp,agendaTipos,agendaTiposData),
      })),
      {tipo:"Otros", color:"var(--text3)", hours:tEntries.filter(a=>!knownTipo(a.type)).reduce((s,a)=>s+(parseFloat(a.hours)||0),0)},
    ].filter(x=>x.hours>0);
    return {name:t.name,color:TEAM_CLR[ti%TEAM_CLR.length],hours:th,byTipo};
  }).filter(t=>t.hours>0).sort((a,b)=>b.hours-a.hours);

  const projMap=new Map();
  agenda.filter(a=>a.external_project_id).forEach(a=>{
    const proj=notionItems.find(i=>i.external_id===a.external_project_id);
    if(!proj) return;
    if(!projMap.has(a.external_project_id)) projMap.set(a.external_project_id,{title:proj.title,icon:proj.icon,hours:0,count:0,tasks:new Map()});
    const entry=projMap.get(a.external_project_id);
    entry.hours+=(parseFloat(a.hours)||0); entry.count+=1;
    const taskId=a.external_task_id||`__desc__${a.description||"—"}`;
    if(!entry.tasks.has(taskId)){
      const taskItem=a.external_task_id&&notionItems.find(i=>i.external_id===a.external_task_id);
      entry.tasks.set(taskId,{title:taskItem?.title||a.description||"Sin tarea específica",icon:taskItem?.icon||null,hours:0,count:0});
    }
    const t=entry.tasks.get(taskId); t.hours+=(parseFloat(a.hours)||0); t.count+=1;
  });
  const projects=[...projMap.values()]
    .map(p=>({...p,taskList:[...p.tasks.values()].sort((a,b)=>b.hours-a.hours)}))
    .sort((a,b)=>b.hours-a.hours);

  const bar = (h,max,color) => `<div class="bar"><div class="fill" style="width:${Math.round(h/(max||1)*100)}%;background:${color}"></div></div>`;

  const dashSections = () => {
    const maxTipo=Math.max(...totalByTipo.map(t=>t.hours),1);
    const maxTeam=Math.max(...teamsData.map(t=>t.hours),1);
    const maxProj=Math.max(...projects.map(p=>p.hours),1);
    let html="";

    // Horas por proyecto Notion con desglose de tareas
    if(projects.length){
      html+=`<h2>Horas por proyecto Notion</h2><div class="rows">`;
      html+=projects.map(p=>{
        const maxTaskH=Math.max(...p.taskList.map(t=>t.hours),1);
        return `<div class="row">
          <div class="rl"><span class="lbl">${p.icon?esc(p.icon)+" ":""}${esc(p.title)}</span><span class="val"><span class="cnt">${p.count} entrada${p.count!==1?"s":""}</span> <b style="color:#0ea5e9">${p.hours.toFixed(1)}h</b></span></div>
          ${bar(p.hours,maxProj,"#0ea5e9")}
          <div class="sub">${p.taskList.map(t=>`<div class="srow">
            <div class="rl"><span class="slbl">${t.icon?esc(t.icon)+" ":"• "}${esc(t.title)}</span><span class="sval"><span class="cnt">${t.count}×</span> <b>${t.hours.toFixed(1)}h</b></span></div>
            <div class="bar sm"><div class="fill" style="width:${Math.round(t.hours/maxTaskH*100)}%;background:#0ea5e9;opacity:.55"></div></div>
          </div>`).join("")}</div>
        </div>`;
      }).join("");
      html+=`</div>`;
    }
    // Productividad — abre el informe: es el KPI que resume el período
    if(prod.index!==null){
      const bandas=[
        {...prodBand(100), horas:prod.alta },
        {...prodBand(50),  horas:prod.media},
        {...prodBand(0),   horas:prod.baja },
      ].filter(b=>b.horas>0);
      const base=prod.horas||1;
      const pb=prodBand(prod.index);
      html+=`<h2>Productividad del tiempo dedicado</h2>
        <div class="prod">
          <div class="pidx" style="color:${pb.color}">${prod.index}%<span>${pb.label}</span></div>
          <div class="pbar">${bandas.map(b=>`<div style="width:${Math.round(b.horas/base*100)}%;background:${b.color}"></div>`).join("")}</div>
          <div class="legend">${bandas.map(b=>`<span><span class="sq" style="background:${b.color}"></span>${b.label} <b>${b.horas.toFixed(1)}h</b> (${Math.round(b.horas/base*100)}%)</span>`).join("")}</div>
          <div class="pnote">${prod.baja>0
            ? `Relación de dedicación <b>${fmtRatio(prod.ratio)}</b> — ${fmtHours(prod.alta)} de alta productividad por cada ${fmtHours(prod.baja)} de baja.`
            : `${fmtHours(prod.alta)} de alta productividad y ninguna hora de baja.`}${prod.sinTipo>0
            ? ` ${fmtHours(prod.sinTipo)} sin tipo quedan fuera del índice.` : ""}</div>
        </div>`;
    }

    // Distribución por tipo
    if(totalByTipo.length){
      html+=`<h2>Distribución por tipo</h2><div class="rows">`;
      html+=totalByTipo.map(t=>{
        // "Otros" no tiene peso: son las horas sin tipo, fuera del índice
        const pb = t.prod===undefined ? null : prodBand(t.prod);
        const tag = pb ? `<span class="tag" style="background:${pb.color}1e;color:${pb.color}">${t.prod}%</span>` : "";
        return `<div class="row">
        <div class="rl"><span class="lbl">${esc(t.tipo)}${tag}</span><span class="val" style="color:${t.color}">${t.hours.toFixed(1)}h</span></div>
        ${bar(t.hours,maxTipo,t.color)}</div>`;
      }).join("");
      html+=`</div>`;
    }

    // Horas por equipo (barra apilada por tipo)
    if(teamsData.length){
      html+=`<h2>Horas por equipo</h2><div class="rows">`;
      html+=teamsData.map(t=>`<div class="row">
        <div class="rl"><span class="lbl"><span class="dot" style="background:${t.color}"></span>${esc(t.name)}</span><span class="val">${t.hours.toFixed(1)}h</span></div>
        <div class="bar stack">${t.byTipo.map(x=>`<div style="width:${Math.round(x.hours/t.hours*100)}%;background:${x.color}" title="${esc(x.tipo)}: ${x.hours.toFixed(1)}h"></div>`).join("")}</div>
        <div class="legend">${t.byTipo.map(x=>`<span><span class="sq" style="background:${x.color}"></span>${esc(x.tipo)} <b>${x.hours.toFixed(1)}h</b></span>`).join("")}</div>
      </div>`).join("");
      html+=`</div>`;
    }

    return html;
  };

  const tableSections = () => Object.entries(byType).map(([tp,es])=>`
    <div class="grp"><span>${esc(tp)}</span><span style="color:#3b82f6;font-weight:800">${es.reduce((s,a)=>s+(parseFloat(a.hours)||0),0).toFixed(1)}h</span></div>
    <table><thead><tr><th>Fecha</th><th>Descripción</th><th>Dpto.</th><th>Prioridad</th><th>Estado</th><th>Horas</th></tr></thead>
    <tbody>${es.map(a=>`<tr>
      <td style="white-space:nowrap;color:#64748b">${fmtD(a.entry_date)}</td>
      <td>${esc(a.description)}</td>
      <td style="color:#64748b">${esc(a.department)}</td>
      <td>${esc(a.priority)}</td>
      <td>${esc(a.status)}</td>
      <td style="font-weight:700;color:#3b82f6">${parseFloat(a.hours||0).toFixed(1)}h</td>
    </tr>`).join("")}</tbody></table>`).join("");

  const buildHTML = () => `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>Cuaderno — ${esc(currentUser?.name)}</title>
<style>
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:Arial,sans-serif;font-size:12px;color:#1e293b;padding:16px}
h1{font-size:18px;font-weight:800;margin-bottom:3px}
.sub-h{font-size:11px;color:#64748b;margin-bottom:16px}
.kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin-bottom:18px}
.kpi{border:1px solid #e2e8f0;border-radius:7px;padding:10px 8px;text-align:center}
.kpi .v{font-size:20px;font-weight:800;color:#086C76}
.kpi .l{font-size:9px;color:#64748b;text-transform:uppercase;letter-spacing:.05em;margin-top:2px}
h2{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:#475569;border-bottom:2px solid #e2e8f0;padding-bottom:5px;margin:16px 0 10px}
table{width:100%;border-collapse:collapse;font-size:11px;margin-bottom:14px}
th{background:var(--surface2);padding:6px 9px;text-align:left;font-weight:700;font-size:9px;text-transform:uppercase;color:#64748b;border-bottom:2px solid #e2e8f0}
td{padding:6px 9px;border-bottom:1px solid #f1f5f9}
tr:nth-child(even) td{background:var(--surface2)}
.grp{background:var(--surface2);padding:5px 9px;font-weight:700;font-size:11px;display:flex;justify-content:space-between;margin-top:4px;border-radius:4px 4px 0 0}
.rows{display:flex;flex-direction:column;gap:11px}
.row{page-break-inside:avoid}
.rl{display:flex;justify-content:space-between;align-items:center;font-size:11px;margin-bottom:4px;gap:8px}
.lbl{font-weight:600;color:#334155;display:flex;align-items:center;gap:5px;overflow:hidden}
.val{font-weight:700;color:#334155;white-space:nowrap;flex-shrink:0}
.cnt{font-size:9.5px;color:#94a3b8;font-weight:500}
.dot{width:8px;height:8px;border-radius:50%;display:inline-block;flex-shrink:0}
.bar{height:7px;background:var(--surface2);border-radius:3px;overflow:hidden}
.bar.sm{height:5px}
.bar.stack{display:flex}
.bar.stack>div{height:100%}
.fill{height:100%;border-radius:3px}
.legend{display:flex;gap:10px;flex-wrap:wrap;margin-top:5px;font-size:10px;color:#475569}
.tag{font-size:8.5px;font-weight:800;padding:1px 5px;border-radius:10px;margin-left:5px}
.prod{page-break-inside:avoid}
.pidx{font-size:26px;font-weight:800;line-height:1;margin-bottom:7px}
.pidx span{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;margin-left:7px}
.pbar{height:12px;border-radius:6px;overflow:hidden;display:flex;background:var(--surface2)}
.pbar>div{height:100%}
.pnote{margin-top:7px;font-size:10.5px;color:#475569;line-height:1.5}
.legend .sq{width:7px;height:7px;border-radius:2px;display:inline-block;margin-right:3px}
.sub{margin-top:7px;padding-left:14px;border-left:2px solid #0ea5e933;display:flex;flex-direction:column;gap:5px}
.srow .rl{font-size:10.5px;margin-bottom:3px}
.slbl{color:#475569;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.sval{white-space:nowrap;flex-shrink:0;color:#475569}
footer{text-align:center;font-size:9px;color:#94a3b8;margin-top:18px;padding-top:8px;border-top:1px solid #e2e8f0}
@media print{body{padding:0}@page{margin:15mm 12mm}}
</style></head><body>
<h1>Cuaderno — ${esc(currentUser?.name)}</h1>
<div class="sub-h">${isDash?"Resumen del dashboard · ":""}Exportado el ${new Date().toLocaleDateString("es-ES",{day:"2-digit",month:"long",year:"numeric"})}</div>
<div class="kpis">
  ${prod.index!==null ? `<div class="kpi"><div class="v" style="color:${prodBand(prod.index).color}">${prod.index}%</div><div class="l">Productividad</div></div>` : ""}
  <div class="kpi"><div class="v">${agenda.length}</div><div class="l">Registros</div></div>
  <div class="kpi"><div class="v">${totalH.toFixed(1)}h</div><div class="l">Horas totales</div></div>
  <div class="kpi"><div class="v">${Object.keys(byType).length}</div><div class="l">Tipos</div></div>
  ${prod.index!==null ? "" : `<div class="kpi"><div class="v">${[...new Set(agenda.map(a=>a.entry_date?.slice(0,7)).filter(Boolean))].length}</div><div class="l">Meses</div></div>`}
</div>
${isDash ? dashSections() : tableSections()}
<footer>ALM Manager · Cuaderno · ${esc(currentUser?.name)}</footer>
</body></html>`;

  const handlePrint = () => {
    const iframe = iframeRef.current;
    if(!iframe) return;
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if(!doc) return;
    doc.open(); doc.write(buildHTML()); doc.close();
    setTimeout(()=>{ try{ iframe.contentWindow.focus(); iframe.contentWindow.print(); }catch(e){} },300);
  };

  return (
    <div>
      <h3 style={{margin:"0 0 16px",fontWeight:700,fontSize:16,display:"flex",alignItems:"center",gap:8}}>
        <Icon name="file-text" size={15} color="var(--accent)"/>Exportar {isDash?"Dashboard":"Cuaderno"}
      </h3>
      <div style={{background:"var(--bg)",borderRadius:10,padding:14,marginBottom:14,border:"1px solid var(--border)"}}>
        <div style={{fontWeight:600,fontSize:12,marginBottom:8}}>El informe incluirá</div>
        {(isDash ? [
          ["clock","var(--accent)", totalH.toFixed(1)+"h en "+agenda.length+" registros"],
          ["link-45deg","#0ea5e9", "Proyectos Notion con desglose de tareas ("+projects.length+")"],
          ...(prod.index!==null ? [["graph-up-arrow", prodBand(prod.index).color,
            "Productividad "+prod.index+"% · "+fmtRatio(prod.ratio)+" alta:baja"]] : []),
          ["bar-chart-fill","#8b5cf6", "Distribución por tipo ("+totalByTipo.length+")"],
          ["people","#22c55e", "Horas por equipo ("+teamsData.length+")"],
        ] : [
          ["journal-text","var(--accent)", agenda.length+" registros"],
          ["clock","#8b5cf6", totalH.toFixed(1)+"h registradas"],
          ["bar-chart-fill","#22c55e", Object.keys(byType).length+" tipos de actividad"],
        ]).map(([ic,cl,txt])=>(
          <div key={txt} style={{display:"flex",alignItems:"center",gap:7,padding:"3px 0",fontSize:12}}>
            <Icon name={ic} size={12} color={cl}/><span>{txt}</span>
          </div>
        ))}
      </div>
      <div style={{background:"var(--info-bg)",borderRadius:9,padding:"10px 13px",marginBottom:16,border:"1px solid #bae6fd",fontSize:11.5,color:"#0369a1"}}>
        Se abrirá el diálogo de impresión. Elige <b>"Guardar como PDF"</b> como destino.
      </div>
      <iframe ref={iframeRef} style={{display:"none"}} title="print-cuaderno"/>
      <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
        <button className="btn" onClick={onClose}
          style={{padding:"8px 16px",borderRadius:9,border:"1px solid var(--border)",cursor:"pointer",fontSize:13,background:"var(--surface2)",color:"var(--text2)",fontFamily:"var(--font)"}}>
          Cerrar
        </button>
        <BtnPrimary onClick={handlePrint}>
          <Icon name="download" size={13} color="#fff"/>Generar PDF
        </BtnPrimary>
      </div>
    </div>
  );
}
